import { Link } from 'react-router-dom';
import { CircleQuestionMark, Highlighter, Search, SquarePen, Wallet } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { LoginArea } from '@/components/auth/LoginArea';
import { FaqDialog } from '@/components/FaqDialog';
import { HighlightComposer } from '@/components/HighlightComposer';
import { OpenAnything } from '@/components/OpenAnything';
import { ThemeToggle } from '@/components/ThemeToggle';
import { WalletModal } from '@/components/WalletModal';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export function AppLogo({ className }: { className?: string }) {
  return (
    <Link
      to="/"
      className={cn(
        'inline-flex items-center gap-2.5 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      <span className="flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-sm">
        <Highlighter className="size-4.5" strokeWidth={2.5} />
      </span>
      <span className="text-xl font-bold tracking-tight">glowmark</span>
    </Link>
  );
}

/** App chrome: sticky header with logo + login, and a footer. */
export function AppShell({ children }: { children: ReactNode }) {
  const [composerOpen, setComposerOpen] = useState(false);
  const [openDialogOpen, setOpenDialogOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur-md">
        <div className="container flex h-14 items-center justify-between gap-4">
          <AppLogo />
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setOpenDialogOpen(true)}
              aria-label="Open an article, note, or profile"
              title="Open an article, note, or profile"
            >
              <Search className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setComposerOpen(true)}
              aria-label="New highlight"
              title="New highlight"
            >
              <SquarePen className="size-4" />
            </Button>
            <WalletModal>
              <Button variant="ghost" size="icon" aria-label="Wallet" title="Wallet">
                <Wallet className="size-4" />
              </Button>
            </WalletModal>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setFaqOpen(true)}
              aria-label="FAQ"
              title="FAQ"
            >
              <CircleQuestionMark className="size-4" />
            </Button>
            <ThemeToggle />
            <LoginArea className="max-w-44" />
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-border/70 mt-16">
        <div className="container py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <p className="flex items-center gap-1.5">
            <Highlighter className="size-3.5 text-primary" />
            Read the best bits.
          </p>
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-foreground transition-colors underline-offset-4 hover:underline"
          >
            Vibed with Shakespeare
          </a>
        </div>
      </footer>

      <HighlightComposer
        open={composerOpen}
        onOpenChange={setComposerOpen}
        source={{ type: 'web' }}
      />

      <FaqDialog open={faqOpen} onOpenChange={setFaqOpen} />

      <Dialog open={openDialogOpen} onOpenChange={setOpenDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Open</DialogTitle>
            <DialogDescription>
              Jump to an article, note, profile, or web page.
            </DialogDescription>
          </DialogHeader>
          <OpenAnything autoFocus onNavigate={() => setOpenDialogOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
